import React, { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form-new';
import {
  Divider,
  IconButton,
  Icons,
  SimpleDialog,
  TextArea,
} from '@nextiva/next-ui-components';
import { StyledIcon } from '../Steps.styled';
import { useWizardContextV3 } from '../WizardContextV3/WizardContextV3';
import {
  CUSTOMER_PROMPTS_FIELDS,
  BRAND_REGISTRATION_FIELDS,
} from '../constants';
import { Body2Typo, SubtitleTypo } from '../WizardV3.styled';

const dataTestId = 'PlatformAdminUI-CustomerPrompts';
const setValueOptions = { shouldValidate: true, shouldDirty: false };
export function CustomerPrompts() {
  const {
    control,
    formState: { errors },
    setValue,
    watch,
    getFieldState,
  } = useWizardContextV3();
  const [brandName, businessNumber] = watch([
    BRAND_REGISTRATION_FIELDS.BRAND_NAME,
    BRAND_REGISTRATION_FIELDS.BUSINESS_NUMBER,
  ]);
  const [optInModal, setOptInModal] = useState(false);
  const [supportModal, setSupportModal] = useState(false);
  const [optOutModal, setOptOutModal] = useState(false);

  const OptingInMessage = `You have successfully been subscribed to messages from ${brandName} from this number. Message frequency may vary. Message and Data Rates may apply. Reply HELP for help. Reply STOP to unsubscribe.`;
  const SupportCommand = `For additional information please call ${brandName} at ${businessNumber}. To opt-out, reply STOP.`;
  const OptOutMessage = `You have successfully been unsubscribed to messages from ${brandName} from this number. Reply START to subscribe again.`;

  useEffect(() => {
    const { isDirty: isOptInDirty } = getFieldState(
      CUSTOMER_PROMPTS_FIELDS.OPT_IN_MESSAGE,
    );
    const { isDirty: isSupportDirty } = getFieldState(
      CUSTOMER_PROMPTS_FIELDS.SUPPORT_MESSAGE,
    );
    const { isDirty: isOptOutDirty } = getFieldState(
      CUSTOMER_PROMPTS_FIELDS.OPT_OUT_MESSAGE,
    );
    if (!isOptInDirty) {
      setValue(
        CUSTOMER_PROMPTS_FIELDS.OPT_IN_MESSAGE,
        OptingInMessage,
        setValueOptions,
      );
    }
    if (!isSupportDirty) {
      setValue(
        CUSTOMER_PROMPTS_FIELDS.SUPPORT_MESSAGE,
        SupportCommand,
        setValueOptions,
      );
    }
    if (!isOptOutDirty) {
      setValue(
        CUSTOMER_PROMPTS_FIELDS.OPT_OUT_MESSAGE,
        OptOutMessage,
        setValueOptions,
      );
    }
  }, []);

  const brandNameValidation = (value) => {
    const brand = watch(BRAND_REGISTRATION_FIELDS.BRAND_NAME);
    return (
      (!!brand && value.includes(brand)) ||
      'Please include your brand name in the sample message.'
    );
  };
  const phoneNumberValidation = (value) => {
    const number = watch(BRAND_REGISTRATION_FIELDS.BUSINESS_NUMBER);
    return (
      (!!number && value.includes(number)) ||
      'Please include your support phone number with the format (XXX) XXX-XXXX in the sample message.'
    );
  };

  return (
    <div>
      <SubtitleTypo mb={2}>
        Please review and understand mandatory customer prompts. You must
        include them in your opt-in and opt-out messages
      </SubtitleTypo>
      <Body2Typo mb={6}>
        Customers should be able to use default keywords <b>‘STOP’</b> to
        opt-out,
        <b>‘HELP’</b> to get support and <b>‘START’</b> to opt-in.
      </Body2Typo>
      <Divider />
      <SubtitleTypo mt={4}>
        Please provide the message customers receive after opting-in *
        <IconButton
          aria-label="customer-optin-tooltip"
          onClick={() => setOptInModal(true)}
          data-testid={`${dataTestId}-Modal-HelpIcon`}
        >
          <StyledIcon icon={Icons.info} />
        </IconButton>
      </SubtitleTypo>
      <SimpleDialog
        open={optInModal}
        title="OPT-IN messaging"
        confirmText="Done"
        confirmButtonProps={{
          color: 'primary',
          onClick: () => setOptInModal(false),
          'data-testid': `${dataTestId}-DoneButton`,
        }}
      >
        <Body2Typo width="347px" mb={3}>
          Regardless of the primary usage of SMS, registration requires opt-in
          message examples. If you would like to use the default verbiage or
          will only be using conversational verbiage, please select the default
          option. If you would like to create your own verbiage, please select
          the custom option. Opt-in confirmation message must include: Brand
          Name and disclosures, opt-out keyword of future messaging, support
          keyword for more information, message frequency, message and data
          rates disclosure.
        </Body2Typo>
      </SimpleDialog>
      <Controller
        control={control}
        name={CUSTOMER_PROMPTS_FIELDS.OPT_IN_MESSAGE}
        rules={{
          required: true,
          minLength: { value: 20, message: 'Minimum 20 characters' },
          maxLength: { value: 1024, message: 'Maximum 1024 characters' },
          validate: { brandNameValidation },
        }}
        render={({ field: { onChange, onBlur, value } }) => {
          return (
            <TextArea
              value={value ? String(value) : ''}
              onChange={onChange}
              onBlur={onBlur}
              charLimit={1024}
              maxRows={5}
              required
              sx={{ width: '495px', mt: '16px', mb: '48px' }}
              error={!!errors.optinMessage}
              helperText={
                errors.optinMessage?.message || 'Minimum 20 characters'
              }
              data-testid={`${dataTestId}-OptInMessage-Textarea`}
            />
          );
        }}
      />
      <Divider />
      <SubtitleTypo mt={4}>
        Please provide the message customers receive as a reply to support
        command *
        <IconButton
          aria-label="customer-supportmessage-tooltip"
          onClick={() => setSupportModal(true)}
          data-testid={`${dataTestId}-Modal-HelpIcon`}
        >
          <StyledIcon icon={Icons.info} />
        </IconButton>
      </SubtitleTypo>
      <SimpleDialog
        open={supportModal}
        title="Support messaging"
        confirmText="Done"
        confirmButtonProps={{
          color: 'primary',
          onClick: () => setSupportModal(false),
          'data-testid': `${dataTestId}-DoneButton`,
        }}
      >
        <Body2Typo width="347px" mb={3}>
          Support message must include: Brand name, customer care information
          (can be phone number, email or link to a support contact page)
        </Body2Typo>
      </SimpleDialog>
      <Controller
        control={control}
        name={CUSTOMER_PROMPTS_FIELDS.SUPPORT_MESSAGE}
        rules={{
          required: true,
          minLength: { value: 20, message: 'Minimum 20 characters' },
          maxLength: { value: 1024, message: 'Maximum 1024 characters' },
          validate: { brandNameValidation, phoneNumberValidation },
        }}
        render={({ field: { onChange, onBlur, value } }) => {
          return (
            <TextArea
              value={value ? String(value) : ''}
              onChange={onChange}
              onBlur={onBlur}
              charLimit={1024}
              maxRows={5}
              required
              sx={{ width: '495px', mt: '16px', mb: '48px' }}
              error={!!errors.supportMessage}
              helperText={
                errors.supportMessage?.message || 'Minimum 20 characters'
              }
              data-testid={`${dataTestId}-CustomerSupportMessage-Textarea`}
            />
          );
        }}
      />
      <Divider />
      <SubtitleTypo mt={4}>
        Please provide the message customers receive after opting out *
        <IconButton
          aria-label="customer-prompts-tooltip"
          onClick={() => setOptOutModal(true)}
          data-testid={`${dataTestId}-OptOut-HelpIcon`}
        >
          <StyledIcon icon={Icons.info} />
        </IconButton>
      </SubtitleTypo>
      <SimpleDialog
        open={optOutModal}
        title="OPT-OUT messaging"
        confirmText="Done"
        confirmButtonProps={{
          color: 'primary',
          onClick: () => setOptOutModal(false),
          'data-testid': `${dataTestId}-DoneButton`,
        }}
      >
        <Body2Typo width="347px" mb={3}>
          Opt-out confirmation must include: Brand name and confirmation that no
          additional messages will be sent.
        </Body2Typo>
      </SimpleDialog>
      <Controller
        control={control}
        name={CUSTOMER_PROMPTS_FIELDS.OPT_OUT_MESSAGE}
        rules={{
          required: true,
          minLength: { value: 20, message: 'Minimum 20 characters' },
          maxLength: { value: 1024, message: 'Maximum 1024 characters' },
          validate: { brandNameValidation },
        }}
        render={({ field: { onChange, onBlur, value } }) => {
          return (
            <TextArea
              value={value ? String(value) : ''}
              onChange={onChange}
              onBlur={onBlur}
              charLimit={1024}
              maxRows={5}
              required
              sx={{ width: '495px', mt: '16px', mb: '64px' }}
              error={!!errors.optoutMessage}
              helperText={
                errors.optoutMessage?.message || 'Minimum 20 characters'
              }
              data-testid={`${dataTestId}-CustomerOptOutMessage-Textarea`}
            />
          );
        }}
      />
    </div>
  );
}
